import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';

import {
  IDataAddress,
  IDataCoordinates,
  IDataSearchType,
  ISearchInfos
} from '@features/eligibility/interfaces';
import { getIPEAndBANAddresses, modifyAddress } from './geoMatchAPI';
import { GeoMatchState, ModifyAddress } from '../geoMatchInterfaces';
import { RootState } from '../../../app/store';

const initialState: GeoMatchState<IDataSearchType> = {
  searchType: '',
  searchData: null,
  lastApiError: '',
  geoMatchListLoading: false,
  geoMatchList: [],
  geoMatchTotalCount: 0,
  IPE: undefined,
  BAN: undefined
};

export const loadIPEAndBANAddresses = createAsyncThunk(
  'geomatch/loadIPEAndBANAddresses',
  async (params: string, { dispatch }) => {
    dispatch(setAPIError(null));
    try {
      const response = await getIPEAndBANAddresses(params);
      return response.data;
    } catch (e) {
      dispatch(setAPIError((e as Error).message));
      throw e;
    }
  }
);

export const buildSearchQueryByInfos = (search: ISearchInfos) => {
  const urlParams = new URLSearchParams();
  urlParams.append('latitude', (search.data as IDataAddress).latitude);
  urlParams.append('longitude', (search.data as IDataAddress).longitude);

  if (search.type === 'coordinates') {
    urlParams.append('distance', (search.data as IDataCoordinates).radius);
  }
  return urlParams.toString();
};

export const fixMismatchRequest = createAsyncThunk(
  'geomatch/fixMismatchRequest',
  async (params: ModifyAddress, { dispatch }) => {
    dispatch(setAPIError(null));
    try {
      const response = await modifyAddress(params);
      return response.data;
    } catch (e) {
      dispatch(setAPIError((e as Error).message));
      throw e;
    }
  }
);

export const selectGeoMatchState = createSelector(
  (state: RootState) => state.geomatch,
  (s) => s
);

export const selectSearchInfos = createSelector(selectGeoMatchState, (s) => ({
  type: s.searchType,
  data: s.searchData
}));
export const selectIPEInfos = createSelector(selectGeoMatchState, (s) => s.IPE);
export const selectBANInfos = createSelector(selectGeoMatchState, (s) => s.BAN);

export const selectLastApiError = createSelector(selectGeoMatchState, (s) => s.lastApiError);

export const geoMatchSlice = createSlice({
  name: 'geomatch',
  initialState,
  reducers: {
    setSearchType: (state, action) => {
      state.searchType = action.payload.type;
      state.searchData = action.payload.data;
    },
    clearSearchType: (state) => {
      state.searchType = '';
      state.searchData = null;
    },
    setIPE: (state, action) => {
      state.IPE = action.payload.address;
    },
    clearIPE: (state) => {
      state.IPE = undefined;
    },
    setBAN: (state, action) => {
      state.BAN = action.payload.address;
    },
    clearBAN: (state) => {
      state.BAN = undefined;
    },
    setAPIError: (state, action) => {
      state.lastApiError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder;
  }
});

export const { setAPIError, clearSearchType, setSearchType, clearIPE, setIPE, clearBAN, setBAN } =
  geoMatchSlice.actions;

export default geoMatchSlice.reducer;
